import cx from 'classnames'
import { useCallback, MouseEvent } from 'react'
import { useLocation } from 'react-router'
import { isNil } from 'lodash'

import { useResponsive } from 'hooks'
import { getI18nPath, Link } from 'features/i18n/router'
import { logEvent } from 'lib/analytics'
import { NavMenu } from 'lib/_types'
import { ROUTE_PATH } from 'lib/paths'

import { ExternalLink } from '..'

import './style.scss'

const Navigation = ({
  menu,
  mode = 'horizontal',
  isLandingPage,
}: {
  menu: NavMenu[]
  mode?: 'vertical' | 'horizontal'
  isLandingPage?: boolean
}) => {
  const location = useLocation()
  const { isDesktop } = useResponsive()

  const isActiveLink = useCallback(
    (link: NavMenu) => {
      if (link.pathKeys) {
        return link.pathKeys.find((pathKey) => {
          let i18nPath = getI18nPath(pathKey.path)

          if (pathKey.mode === 'startsWith') {
            if (i18nPath.length > 1 && i18nPath.endsWith('/')) {
              i18nPath = i18nPath.substring(0, i18nPath.length - 1)
            }

            return location.pathname.startsWith(i18nPath)
          }

          return location.pathname === i18nPath
        })
      }

      const i18nPath = getI18nPath(link.to)
      if (link.mode === 'startsWith') {
        return location.pathname.startsWith(i18nPath)
      }

      return i18nPath === location.pathname
    },
    [location]
  )

  return (
    <ul className={cx('Navigation', `Navigation--${mode}`)}>
      {menu
        .filter(({ displayIn }) => {
          if (isNil(displayIn)) {
            return true
          }

          return (
            (displayIn == 'desktop' && isDesktop) ||
            (displayIn == 'mobile' && !isDesktop)
          )
        })
        .map((menu) => {
          const onClick = (e: MouseEvent<HTMLElement>) => {
            if (typeof menu.onClick === 'function') {
              if (!menu.to) {
                e.preventDefault()
              }

              menu.onClick()
            }

            if ((menu.to || '').match(/\?modalMode=/) === null) {
              window.scrollTo(0, 0)
            }

            if (menu.logKey) {
              logEvent.navigation_pane_select({ destination: menu.logKey })
            }
          }
          const className = cx('Navigation__a', {
            [`Navigation__a--${menu.key}`]: true,
            'Navigation__a--active': isActiveLink(menu),
            'Navigation__a--white': isLandingPage,
          })
          const to = menu.to || ROUTE_PATH.HOME
          return (
            <li
              key={menu.key}
              className={cx('Navigation__li', {
                [`Navigation__li--${menu.className}`]: menu.className,
              })}
            >
              {menu.isExternal ? (
                <ExternalLink href={to} className={className} onClick={onClick}>
                  {menu.label}
                </ExternalLink>
              ) : (
                <Link to={to} className={className} onClick={onClick}>
                  {menu.label}
                </Link>
              )}
            </li>
          )
        })}
    </ul>
  )
}

export default Navigation
